import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { reorderItems } from "@/lib/reorder";
import { FavoritesGrid } from "@/components/FavoritesGrid";
import { MyInvoices } from "@/components/MyInvoices";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { StatusPill } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Package, RotateCcw, Mail, Phone, Award, Copy } from "lucide-react";
import { OpenInAdmin } from "@/components/OpenInAdmin";
import { PushBell } from "@/components/PushBell";

export default function Account() {
  const { user } = useAuth();
  const { addItem, setDrawerOpen } = useCart();
  const qc = useQueryClient();
  const approveSheet = async (token) => {
    try { await api.post(`/approvals/${token}/approve`); toast.success("Approved — thank you!"); qc.invalidateQueries({ queryKey: ["myorders"] }); }
    catch { toast.error("Could not approve"); }
  };
  const requestChanges = async (token) => {
    const fb = window.prompt("What would you like changed?");
    if (fb == null) return;
    try { await api.post(`/approvals/${token}/request-changes`, { feedback: fb }); toast.success("Sent — we'll be in touch"); qc.invalidateQueries({ queryKey: ["myorders"] }); }
    catch { toast.error("Could not send"); }
  };
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["myorders"], queryFn: async () => (await api.get("/orders/mine")).data });
  const { data: loyalty } = useQuery({ queryKey: ["loyalty"], queryFn: async () => (await api.get("/loyalty/me")).data });

  const reorder = (order) => reorderItems(order.items, addItem, setDrawerOpen);
  const reorderOne = (it) => reorderItems([it], addItem, setDrawerOpen);

  const goal = loyalty?.goal || 10;
  const punches = loyalty?.punches || 0;
  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); };
  useEffect(() => { if (window.location.hash === "#punch-card" && loyalty) document.getElementById("punch-card")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [loyalty]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center gap-4">
        {user?.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full" /> : <div className="w-14 h-14 rounded-full bg-[#ff6a13] flex items-center justify-center font-display font-black text-xl text-white">{(user?.name || user?.email || "?")[0].toUpperCase()}</div>}
        <div>
          <h1 className="font-display text-2xl font-extrabold text-white">{user?.name || "My Account"}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mt-1">
            <span className="flex items-center gap-1.5"><Mail size={14} />{user?.email}</span>
            {user?.phone && <span className="flex items-center gap-1.5"><Phone size={14} />{user.phone}</span>}
          </div>
          <div className="mt-2"><PushBell email={user?.email} /></div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#1a1410] to-[#12141a] border border-[#ff6a13]/30 rounded-2xl p-6 mt-8 scroll-mt-24" id="punch-card" data-testid="punch-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Award size={18} className="text-[#ff6a13]" />
            <h2 className="font-display text-lg font-bold text-white">Loyalty Punch Card</h2>
          </div>
          <span className="text-xs text-zinc-400 font-mono-p" data-testid="punch-progress">{loyalty?.complete ? "Card complete!" : `${punches} of ${goal} orders`}</span>
        </div>
        <p className="text-sm text-zinc-400 mt-1">{loyalty?.complete ? "Your card is full — your reward is ready to use below." : `Complete ${goal} paid orders and earn 5% off your next order.`}</p>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="punch-dots">
          {Array.from({ length: goal }).map((_, i) => {
            const filled = loyalty?.complete || i < punches;
            return (
              <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center border text-xs font-mono-p ${filled ? "bg-[#ff6a13] border-[#ff6a13] text-white" : "border-zinc-700 text-zinc-600"}`} data-testid={`punch-${i}`}>
                {filled ? <Award size={14} /> : i + 1}
              </div>
            );
          })}
        </div>
        {loyalty?.available_rewards?.length > 0 && (
          <div className="mt-5 border-t border-zinc-800 pt-4" data-testid="punch-rewards">
            <p className="text-xs text-[#10b981] font-mono-p uppercase tracking-wider mb-2">Your rewards — ready to use</p>
            <div className="flex flex-wrap gap-2">
              {loyalty.available_rewards.map((r) => (
                <button key={r.code} onClick={() => copyCode(r.code)} data-testid={`reward-code-${r.code}`} className="inline-flex items-center gap-2 rounded-full border border-[#10b981]/40 bg-[#10b981]/10 px-3 py-1.5 text-sm text-[#10b981] font-mono-p hover:bg-[#10b981]/20">
                  {r.code} · {r.value}% off <Copy size={13} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <FavoritesGrid />
      <MyInvoices />
      <ChangePasswordCard />

      <h2 className="font-display text-xl font-bold text-white mt-10 mb-4">Order history</h2>
      {isLoading ? <p className="text-zinc-500 font-mono-p">Loading…</p> : orders.length === 0 ? (
        <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-10 text-center" data-testid="no-orders">
          <Package className="mx-auto text-zinc-600" size={40} />
          <p className="text-zinc-400 mt-3">No orders yet.</p>
          <Link to="/catalog"><Button className="mt-4 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full">Start Shopping</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.order_id} className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5" data-testid="account-order">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div><p className="font-mono-p font-bold text-white">{o.order_number}</p><p className="text-xs text-zinc-500 font-mono-p">{new Date(o.created_at).toLocaleDateString()}</p></div>
                <div className="flex items-center gap-2"><OpenInAdmin orderNumber={o.order_number} size="sm" /><StatusPill status={o.status} /></div>
              </div>
              {o.status === "cancelled" && (
                <div className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3" data-testid="account-cancelled-block">
                  {o.cancel_reason && <p className="text-xs text-red-400 font-mono-p" data-testid="account-cancel-reason">Reason: {o.cancel_reason}</p>}
                  <Button size="sm" onClick={() => reorder(o)} data-testid="account-reorder-cancelled" className="mt-2 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full font-semibold"><RotateCcw size={14} className="mr-1" />Reorder these items</Button>
                </div>
              )}
              <div className="mt-4 space-y-2">
                {o.items.map((it, i) => (
                  <div key={`${it.name}-${i}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 checker rounded overflow-hidden flex items-center justify-center shrink-0">
                      {(it.mockup_render_url || it.image_url || it.preview_url) && <img src={fileUrl(it.mockup_render_url || it.preview_url || it.image_url)} alt="" className="max-w-full max-h-full object-contain" />}
                    </div>
                    <span className="flex-1 text-sm text-zinc-300 truncate">{it.name}</span>
                    <span className="text-xs text-zinc-500 font-mono-p">×{it.quantity}</span>
                    {o.status !== "cancelled" && (it.image_url || it.preview_url) && (
                      <button onClick={() => reorderOne(it)} title="Reorder this design" data-testid={`account-reorder-line-${i}`} className="text-[#22c3d6] hover:text-white transition-colors shrink-0"><RotateCcw size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
              {o.items.map((it, i) => (it.added_by_admin && it.approval_token && it.approval_status !== "approved") ? (
                <div key={`ap-${i}`} className="mt-3 rounded-lg border border-[#ff6a13]/40 bg-[#ff6a13]/10 p-3" data-testid="account-approval">
                  <p className="text-sm text-white font-semibold">Approval needed: {it.name}</p>
                  <p className="text-xs text-zinc-400 mt-0.5 font-mono-p">{it.sheet_width}" × {it.sheet_height}" · ${((it.unit_price || 0) * (it.quantity || 1)).toFixed(2)}</p>
                  {it.approval_status === "changes_requested" && <p className="text-xs text-[#ffd600] mt-1">Changes requested — we'll follow up. You can still approve once ready.</p>}
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => approveSheet(it.approval_token)} data-testid="account-approve-btn" className="bg-[#10b981] hover:bg-[#0e9e70] rounded-full font-semibold">Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => requestChanges(it.approval_token)} data-testid="account-request-changes-btn" className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800">Request changes</Button>
                  </div>
                </div>
              ) : null)}
              <div className="flex items-center justify-between border-t border-zinc-800 mt-4 pt-4">
                <span className="font-bold font-mono-p text-white">${o.total.toFixed(2)}</span>
                {o.status !== "cancelled" && (
                  <Button size="sm" variant="outline" onClick={() => reorder(o)} className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800" data-testid="reorder-btn"><RotateCcw size={14} className="mr-1" />Reorder</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
